import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { Base64 } from 'js-base64';
import { TbAuthService } from '@tb/auth';

// ================================================================================
interface CacheContent {
    expiry: number;
    value: any;
}

// ================================================================================
@Injectable({
    providedIn: 'root',
})
export class MiniStorageService {
    readonly DEFAULT_MAX_AGE: number = 30; // cache age expressed in minutes

    private cache: Map<string, CacheContent>;
    cachedEncoded: string;

    // --------------------------------------------------------------------------------
    constructor(private authService: TbAuthService) {
        this.cache = new Map<string, CacheContent>();
    }

    // --------------------------------------------------------------------------------
    encodeKey(key: string): string {
        return Base64.encode(key);
    }

    // --------------------------------------------------------------------------------
    get(key: string): Observable<any> {
        let cachedItem: CacheContent;
        const encodedKey = this.encodeKey(key);
        let needToUpdateLocalCache = false;

        if (!this.cache.has(encodedKey)) {
            if (this.authService.isSessionStorage()) {
                this.cachedEncoded = sessionStorage.getItem(encodedKey);
            } else {
                this.cachedEncoded = localStorage.getItem(encodedKey);
            }

            if (this.cachedEncoded === null) {
                return of(undefined);
            }

            try {
                const cacheDecoded = Base64.decode(this.cachedEncoded);
                cachedItem = JSON.parse(cacheDecoded);
                needToUpdateLocalCache = true;
            } catch (exc) {
                cachedItem = undefined;
            }
        } else {
            cachedItem = this.cache.get(encodedKey);
        }

        if (cachedItem === undefined) {
            return of(undefined);
        }

        let dateNow = new Date();
        let dateCheck = new Date(cachedItem.expiry);

        if (dateNow <= dateCheck) {
            if (needToUpdateLocalCache) {
                this.set(encodedKey, cachedItem.value, cachedItem.expiry);
            }
            return of(cachedItem.value);
        } else {
            return of(undefined);
        }
    }

    // --------------------------------------------------------------------------------
    set(key: string, value: any, maxAgeInMinutes: number = this.DEFAULT_MAX_AGE) {
        const encodedKey = this.encodeKey(key);
        const expiryDate = new Date();
        expiryDate.setMinutes(expiryDate.getMinutes() + maxAgeInMinutes);

        const itemToStore = { value: value, expiry: Number(expiryDate) };
        this.cache.set(encodedKey, itemToStore);

        try {
            const itemToPersist: string = JSON.stringify(itemToStore);
            if (this.authService.isSessionStorage()) {
                sessionStorage.setItem(encodedKey, Base64.encode(itemToPersist));
            } else {
                localStorage.setItem(encodedKey, Base64.encode(itemToPersist));
            }
        } catch (exc) {
            //console.log('MiniStorage.set got an exception ' + exc);
        }
    }

    // --------------------------------------------------------------------------------
    remove(key: string) {
        const encodedKey = this.encodeKey(key);
        if (this.cache.has(encodedKey)) {
            this.cache.delete(key);
        }
        localStorage.removeItem(encodedKey);
        sessionStorage.removeItem(encodedKey);
    }

    // --------------------------------------------------------------------------------
    clearAll() {
        if (this.cache.size === 0) {
            return;
        }
        this.cache.forEach((value, key) => {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
        });
        this.cache.clear();
    }
}
